import { isString } from 'lodash';

import { matchesAirror, NO_AUTH_HEADER, NOT_AUTHENTICATED } from '@air/errors';

import { NO_CURRENT_USER_ERROR } from '~/swr-hooks/account/useAccount';

/**
 * The request carried a token and the backend rejected it — an expired or
 * revoked session. Recovering means logging the stale session out first.
 */
export const isNotAuthenticatedError = (error: unknown): boolean => {
  if (matchesAirror(error, NOT_AUTHENTICATED)) {
    return true;
  }

  if (isString(error)) {
    return error.includes(NOT_AUTHENTICATED.message);
  }

  if (error instanceof Error) {
    return error.message.includes(NOT_AUTHENTICATED.message);
  }

  return false;
};

/**
 * There was no token to send at all — `Auth.getJWTToken()` threw and `@air/api`
 * raised `NO_AUTH_HEADER` before the request left the browser. Nothing to log
 * out of; the user just needs to sign in.
 */
export const isMissingAuthError = (error: unknown): boolean => {
  if (matchesAirror(error, NO_AUTH_HEADER)) {
    return true;
  }

  if (isString(error)) {
    return error.includes(NO_AUTH_HEADER.message) || error.includes(NO_CURRENT_USER_ERROR);
  }

  if (error instanceof Error) {
    return error.message.includes(NO_AUTH_HEADER.message) || error.message.includes(NO_CURRENT_USER_ERROR);
  }

  return false;
};

/**
 * Either flavor of "you are not signed in". Use when the caller only needs to
 * tell an auth failure apart from a domain failure (e.g. picking error copy),
 * not to decide how to recover from it.
 */
export const isUnauthenticatedError = (error: unknown): boolean =>
  isNotAuthenticatedError(error) || isMissingAuthError(error);
